import { Notification } from '@app/components/various/Notification';
import { NotificationAction } from '@app/components/various/Notification/NotificationAction';
import { getFailedProducts, getStockTypeLabels, showModal } from '@app/ducks';
import { getHasEtaInSelectedDeliveryWindows } from '@app/logic/eta';
import ProductsLogic from '@app/logic/products';
import { isDefined, isNull } from '@app/utils/is';
import { isEmpty } from '@app/utils/isEmpty';
import { Order } from '@typings';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { CancelledProductsNotification } from '../CancelledProductsNotification';
import { ExpiredDelwinNotification } from '../ExpiredDelwinNotification';
import { MissingProductsModal } from '../MissingProductsModal';

import { useOrderProductsContext } from './OrderProductsContext';

interface Props {
  orderDetails: Order.Single;
  deletedProducts: Order.DeletedProduct[] | undefined;
  allProducts: Order.Single['products'];
}

export const OrderProductsAlerts = ({ orderDetails, deletedProducts, allProducts }: Props) => {
  const { t } = useTranslation(['common', 'products', 'selections']);

  const dispatch = useDispatch();
  const { orderDeliveryWindows, sortedProducts, stockTypeGroupsToDisplay } = useOrderProductsContext();
  const failedProductsData = useSelector(getFailedProducts);
  const stockTypeLabels = useSelector(getStockTypeLabels);
  const hasVariantsWithEta = React.useMemo(
    () =>
      getHasEtaInSelectedDeliveryWindows(
        orderDeliveryWindows.map(deliveryWindow => deliveryWindow.deliveryWindow),
        sortedProducts,
      ),
    [sortedProducts, orderDeliveryWindows],
  );

  const showMissingProductsDetails = React.useCallback(() => {
    dispatch(showModal('MissingProductsModal'));
  }, [dispatch]);

  const cancelledProducts = orderDetails.order.products.filter(product => product.isCancelled);
  const hasCancelledProducts = !isEmpty(cancelledProducts);
  const shouldShowMissingProductsAlert =
    !isNull(failedProductsData) && isDefined(failedProductsData.failedProducts) && !isEmpty(failedProductsData.failedProducts);
  const hasDeletedProducts = isDefined(orderDetails.order.modifications) && isDefined(orderDetails.order.modifications.deletedProducts);
  const isMixedAvailabilityAlertVisible = stockTypeGroupsToDisplay.length > 1;

  return (
    <div className="mx-auto my-8 not-empty:grid not-empty:gap-4">
      {shouldShowMissingProductsAlert && (
        <Notification type="warning" message={t('products:missing_products')}>
          <NotificationAction onClick={showMissingProductsDetails}>{t('common:show_details')}</NotificationAction>
          <MissingProductsModal failedProductsData={failedProductsData} />
        </Notification>
      )}
      <ExpiredDelwinNotification order={orderDetails.order} />
      {hasCancelledProducts && <CancelledProductsNotification orderProducts={cancelledProducts} />}
      {hasVariantsWithEta && <Notification type="info" message={t('products:variants_availability_info')} />}
      {isMixedAvailabilityAlertVisible && <Notification message={t('products:mixed_availability', stockTypeLabels)} type="info" />}
      {hasDeletedProducts && isDefined(deletedProducts) && (
        <div>
          <Notification type="warning" message={<b>{t('selections:products_removed_from_selection')}:</b>}>
            <div>
              {deletedProducts.map(product => {
                const productId = product.product;
                const productData = Array.isArray(allProducts) ? null : allProducts[productId];

                if (!isDefined(productData)) {
                  return null;
                }

                const variants = ProductsLogic.getAllVariantsInfoFromProductInfo(productData);
                const variant = variants[product.variant];
                const key = `${product.product}-${product.variant}-${product.deliveryWindow}`;

                return (
                  <span key={key} className='[&:not(:first-child)]:pl-2 [&:not(:last-child)]:after:content-[","]'>
                    {productData.name} {variant && variant.variantName && `- ${variant.variantName}`}
                  </span>
                );
              })}
            </div>
          </Notification>
        </div>
      )}
    </div>
  );
};
